/* GEOTECH · ARC header bridge
   Enhances Dawn's <header-drawer> (mobile menu) without editing global.js:
   - reliable slide-in, and a real slide-out (Dawn removes the drawer after a fixed 400ms)
   - drawer logo + backdrop + menu items animate with the drawer
   - submenu panels stay in place while the whole drawer slides out
   - theme editor: re-opens the menu after a live settings refresh so changes stay visible */
(() => {
  if (customElements.get('geo-header-bridge')) return;

  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const isDesignMode = () => Boolean(window.Shopify && window.Shopify.designMode);
  let reopenMenuAfterEditorRefresh = false;

  class GeoHeaderBridge extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;

      this.section = this.closest('.shopify-section') || document;
      this.shell = document.getElementById(this.dataset.shellId);
      this.backdrop = document.getElementById(this.dataset.backdropId);
      this.headerDrawer = this.shell && this.shell.querySelector('header-drawer');
      this.details = this.headerDrawer && this.headerDrawer.querySelector('#Details-menu-drawer-container');

      this.patchDesktopMenus();
      if (!this.details) return;

      this.panel = this.details.querySelector('.menu-drawer');
      this.summary = this.details.querySelector(':scope > summary');

      this.injectBrand();
      this.patchHeaderDrawer();

      this.observer = new MutationObserver((records) => this.onDetailsMutation(records));
      this.observer.observe(this.details, { attributes: true, attributeFilter: ['open', 'class'] });

      this.onBackdropClick = () => {
        if (this.details.classList.contains('menu-opening') && this.summary) this.summary.click();
      };
      this.onResize = () => {
        if (window.innerWidth >= 990 && this.details.hasAttribute('open') && this.summary) this.summary.click();
      };
      if (this.backdrop) this.backdrop.addEventListener('click', this.onBackdropClick);
      window.addEventListener('resize', this.onResize, { passive: true });

      this.sync();

      if (reopenMenuAfterEditorRefresh) {
        reopenMenuAfterEditorRefresh = false;
        this.openInstantly();
      }
    }

    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
      if (this.backdrop) this.backdrop.removeEventListener('click', this.onBackdropClick);
      window.removeEventListener('resize', this.onResize);
      clearTimeout(this.unlockTimer);
      this.setPageLocked(false);
    }

    get duration() {
      if (reducedMotion.matches) return 0;
      const probe = this.panel || this.shell;
      const value = probe ? getComputedStyle(probe).getPropertyValue('--geo-mobile-drawer-duration').trim() : '';
      const ms = value.endsWith('ms') ? parseFloat(value) : parseFloat(value) * 1000;
      return Number.isFinite(ms) ? ms : 220;
    }

    patchDesktopMenus() {
      if (!this.shell) return;
      this.shell.querySelectorAll('header-menu').forEach((menu) => {
        if (menu.geoPatched || !menu.mainDetailsToggle) return;
        menu.geoPatched = true;
        const details = menu.mainDetailsToggle;
        const summary = details.querySelector('summary');

        const animateClose = () => {
          if (!details.hasAttribute('open') || details.classList.contains('geo-menu-closing')) return;
          if (summary) summary.setAttribute('aria-expanded', 'false');
          if (reducedMotion.matches) {
            details.removeAttribute('open');
            return;
          }
          details.classList.add('geo-menu-closing');
          setTimeout(() => {
            details.classList.remove('geo-menu-closing');
            details.removeAttribute('open');
          }, 160);
        };

        menu.close = animateClose;
        if (summary) {
          summary.addEventListener('click', (event) => {
            if (!details.hasAttribute('open')) {
              details.classList.remove('geo-menu-closing');
              return;
            }
            event.preventDefault();
            animateClose();
          });
        }
      });
    }

    injectBrand() {
      const template = document.getElementById(this.dataset.brandTemplateId);
      const navigation = this.details.querySelector('.menu-drawer__navigation');
      if (template && navigation && !navigation.querySelector('.geo-mobile-drawer-brand')) {
        navigation.prepend(template.content.cloneNode(true));
      }
    }

    patchHeaderDrawer() {
      const drawer = this.headerDrawer;
      if (!drawer || drawer.geoPatched || typeof drawer.closeAnimation !== 'function') return;
      drawer.geoPatched = true;
      const bridge = this;

      // Dawn removes [open] after a hard-coded 400ms. Wait for our own slide-out instead.
      drawer.closeAnimation = function geoCloseAnimation(detailsElement) {
        clearTimeout(detailsElement.geoCloseTimer);
        detailsElement.geoCloseTimer = setTimeout(() => {
          if (detailsElement.classList.contains('menu-opening')) return;
          detailsElement.removeAttribute('open');
          if (detailsElement === bridge.details) bridge.releaseHeldSubmenus();
          const parent = detailsElement.parentElement && detailsElement.parentElement.closest('details[open]');
          if (parent && typeof trapFocus === 'function') trapFocus(parent, detailsElement.querySelector('summary'));
        }, bridge.duration);
      };

      // Keep an open submenu panel in place while the whole drawer slides away.
      const originalClose = drawer.closeMenuDrawer.bind(drawer);
      drawer.closeMenuDrawer = function geoCloseMenuDrawer(event, elementToFocus) {
        const held = [...bridge.details.querySelectorAll('details[open]')].map((element) => ({
          element,
          opening: element.classList.contains('menu-opening'),
          parentMenu: element.closest('.has-submenu.submenu-open'),
        }));
        originalClose(event, elementToFocus);
        if (bridge.details.classList.contains('menu-opening') || !held.length) return;
        held.forEach(({ element, opening, parentMenu }) => {
          element.setAttribute('open', '');
          element.classList.toggle('menu-opening', opening);
          if (parentMenu) parentMenu.classList.add('submenu-open');
        });
        bridge.heldSubmenus = held;
      };
    }

    releaseHeldSubmenus() {
      (this.heldSubmenus || []).forEach(({ element, parentMenu }) => {
        element.removeAttribute('open');
        element.classList.remove('menu-opening');
        const summary = element.querySelector('summary');
        if (summary) summary.setAttribute('aria-expanded', 'false');
        if (parentMenu) parentMenu.classList.remove('submenu-open');
      });
      this.heldSubmenus = null;
    }

    onDetailsMutation(records) {
      this.sync();
    }

    sync() {
      const opening = this.details.classList.contains('menu-opening');
      const open = this.details.hasAttribute('open');

      if (this.backdrop) {
        this.backdrop.classList.toggle('is-visible', opening);
        this.backdrop.setAttribute('aria-hidden', opening ? 'false' : 'true');
        this.backdrop.tabIndex = opening ? 0 : -1;
      }
      if (this.shell) this.shell.classList.toggle('geo-mobile-nav-open', open);

      clearTimeout(this.unlockTimer);
      if (opening || open) this.setPageLocked(true);
      if (!open) this.setPageLocked(false);
      else if (!opening) this.unlockTimer = setTimeout(() => this.setPageLocked(false), this.duration);
    }

    setPageLocked(locked) {
      document.documentElement.classList.toggle('geo-mobile-nav-open', locked);
      document.body.classList.toggle('geo-mobile-nav-open', locked);
    }

    openInstantly() {
      if (!this.summary || this.details.hasAttribute('open')) return;
      this.details.classList.add('geo-no-transition');
      this.summary.click();
      requestAnimationFrame(() => requestAnimationFrame(() => this.details.classList.remove('geo-no-transition')));
    }

    isMenuOpen() {
      return this.details && this.details.hasAttribute('open');
    }
  }

  customElements.define('geo-header-bridge', GeoHeaderBridge);

  document.addEventListener('shopify:section:unload', (event) => {
    const bridge = event.target && event.target.querySelector && event.target.querySelector('geo-header-bridge');
    if (bridge && isDesignMode() && bridge.isMenuOpen()) reopenMenuAfterEditorRefresh = true;
  });
})();
